import * as React from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import {
    type AusschnittEintrag,
    type BearbeitungAktion,
    type BearbeitungStand,
    type Markierung,
    type PdfRect,
} from "./pdfBearbeitung";
import { textRechteckeDerSelection } from "./auswahlText";
import { inhaltsBox, type SeitenAnsichtLike, type ViewerLike } from "./EntfernenEbene";
import { auswahlZuRechtecken, cssZuPdfRect, normalisiere, pdfZuCssRect } from "./pdfKoordinaten";
import { autoScrollSchritt, zielDesAusschnitts } from "./ausschnittZiel";
import { schneidet } from "./zeilen";
import "./pdfEditor.css";
import { alleDokumente, alleFenster } from "../../windowManager/browserFenster/fensterRegister";

export const HINWEIS_GEAENDERTE_ZEILE =
    "Im Bereich liegt eine geänderte Zeile — erst speichern oder die Änderung zurücknehmen.";
export const HINWEIS_ANMERKUNGEN = "Unterschriften, Stempel und Formularfelder im Bereich bleiben an ihrem Platz.";
export const HINWEIS_MARKIERT =
    "Bereich markiert — Ausschneiden verschiebt ihn als Bild, Entfernen löscht ihn beim Speichern, Esc verwirft. Ziehen am Rahmen verschiebt die Markierung, die Ecken ändern ihre Größe.";

/** PDF.js-Anmerkungen (Formularfelder, Stempel …) und eingefügte Editor-Elemente einer Seite. */
const ANMERKUNGEN = ".annotationLayer > section, .annotationEditorLayer > div";

const ueberlappt = (a: DOMRect, b: { left: number; top: number; right: number; bottom: number }) =>
    a.width > 0 && a.height > 0 && a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

/**
 * Ausschneiden & Verschieben (PO 28.09.2026: „neben Entfernen auch einfach
 * Ausschneiden, um das ausgeschnittene Element zu verschieben“).
 *
 * 1. Bereich aufziehen (über Text mit Alt) → erst ein **markierter Bereich**
 *    mit Ecken-Griffen und einer kleinen Leiste darüber: **Ausschneiden**,
 *    **Entfernen**, **Verwerfen** (PR 5, 02.10.2026 — „erst markieren, dann
 *    entscheiden“). Ziehen am Rahmen verschiebt die Markierung, die Ecken
 *    ändern ihre Größe, Entf/Esc verwerfen, Enter schneidet aus. Die
 *    Markierung ist noch keine Bearbeitung (nicht im Stand, nicht im
 *    Rückgängig-Stapel). Textauswahl + Strg+X schneidet direkt aus.
 * 2. Ausschneiden: die Quelle wird zum Entfernen markiert (gestrichelter Rahmen
 *    „Ursprung“ — keine rote Schraffur, verschwinden soll sie ja nicht
 *    ersatzlos), darüber liegt ein bewegliches Element mit dem aufgenommenen
 *    Bild (`aufnehmen`, 3-fache Auflösung). Entfernen legt stattdessen eine
 *    gewöhnliche Entfernen-Markierung an (wie das Werkzeug „Entfernen“).
 * 3. Ziehen mit der Maus, auch auf eine andere Seite; Pfeiltasten 1 pt,
 *    Umschalt+Pfeil 10 pt; Ecken-Griff skaliert proportional; Entf nimmt das
 *    Ausschneiden ganz zurück.
 *
 * Beim Speichern entfernt der Server die Quelle echt und zeichnet das Bild an
 * die Zielstelle — verschobener Text ist danach ein Bild, nicht mehr durchsuchbar.
 *
 * Das bewegliche Element ist ein eigenes Overlay, KEIN PDF.js-Stempel: PDF.js-
 * Annotationen, die eine Entfernen-Markierung berühren, löscht der Server — und
 * der Ausschnitt liegt anfangs genau über seiner Quelle.
 */

const MIN_ZUG = 4;
/** Unter so viel CSS-px Bewegung ist ein Klick ein Klick (Auswählen), kein Verschieben. */
const MIN_VERSCHIEBUNG = 2;
const RENDER_EREIGNISSE = ["pagerendered", "textlayerrendered", "scalechanging", "rotationchanging", "pagesinit"];

let zaehler = 0;
const neueId = () => `a${Date.now().toString(36)}${(++zaehler).toString(36)}`;

const istEingabe = (ziel: EventTarget | null) => {
    const el = ziel as HTMLElement | null;
    if (!el || typeof el.closest !== "function") return false;
    // Auch `plaintext-only` — so tippt man in der Kommentarblase und im Text-bearbeiten-Kasten.
    return !!el.closest("input, textarea, select, [contenteditable]:not([contenteditable='false'])");
};

const auswahlImViewer = (container: HTMLElement | null): Selection | null => {
    const sel = window.getSelection?.();
    if (!container || !sel || sel.isCollapsed || sel.rangeCount === 0) return null;
    const drin = (n: Node | null) => !!n && container.contains(n);
    return drin(sel.anchorNode) || drin(sel.focusNode) ? sel : null;
};

/** Umhüllendes Rechteck mehrerer Userspace-Rechtecke. */
const umhuellende = (rects: readonly PdfRect[]): PdfRect => [
    Math.min(...rects.map((r) => r[0])),
    Math.min(...rects.map((r) => r[1])),
    Math.max(...rects.map((r) => r[2])),
    Math.max(...rects.map((r) => r[3])),
];

const drehungVon = (ansicht: SeitenAnsichtLike): number =>
    ((((ansicht.viewport as { rotation?: number }).rotation ?? 0) % 360) + 360) % 360;

/** Einheitsvektor im Userspace für eine Bildschirmrichtung (dx, dy) — gilt auch für gedrehte Ansichten. */
const richtungImUserspace = (ansicht: SeitenAnsichtLike, dx: number, dy: number): [number, number] => {
    const [ax, ay] = ansicht.viewport.convertToPdfPoint(0, 0);
    const [bx, by] = ansicht.viewport.convertToPdfPoint(dx, dy);
    const laenge = Math.hypot(bx - ax, by - ay) || 1;
    return [(bx - ax) / laenge, (by - ay) / laenge];
};

const r2 = (n: number) => Math.round(n * 100) / 100;

type Zug = { seite: number; x0: number; y0: number; x1: number; y1: number };
/** Aufgezogener, noch nicht entschiedener Bereich (Userspace, damit Zoom und Drehung ihn nicht verschieben). */
type Markiert = { seite: number; rect: PdfRect };
type Ecke = "nw" | "ne" | "sw" | "se";
const ECKEN: readonly Ecke[] = ["nw", "ne", "sw", "se"];
const ECKEN_LAGE: Record<Ecke, React.CSSProperties> = {
    nw: { left: -5, top: -5, cursor: "nwse-resize" },
    ne: { right: -5, top: -5, cursor: "nesw-resize" },
    sw: { left: -5, bottom: -5, cursor: "nesw-resize" },
    se: { right: -5, bottom: -5, cursor: "nwse-resize" },
};
/** Laufende Maus-Aktion auf einem Ausschnitt: Verschieben (Versatz) oder Skalieren (Faktor). */
type Geste = { id: string; dx: number; dy: number; faktor: number };

const stil = {
    element: {
        position: "absolute",
        boxSizing: "border-box",
        outline: "1px dashed rgba(15, 108, 189, 0.9)",
        cursor: "move",
        touchAction: "none",
    } as React.CSSProperties,
    elementAusgewaehlt: { outline: "2px solid rgb(15, 108, 189)", outlineOffset: "1px" } as React.CSSProperties,
    griff: {
        position: "absolute",
        right: -6,
        bottom: -6,
        width: 12,
        height: 12,
        boxSizing: "border-box",
        background: "#fff",
        border: "2px solid rgb(15, 108, 189)",
        borderRadius: 2,
        cursor: "nwse-resize",
        touchAction: "none",
    } as React.CSSProperties,
    ursprung: {
        position: "absolute",
        boxSizing: "border-box",
        border: "1px dashed rgba(96, 96, 96, 0.9)",
        // Leichter Schleier statt Schraffur: „hier war etwas, es wandert“.
        background: "rgba(255, 255, 255, 0.55)",
        pointerEvents: "none",
    } as React.CSSProperties,
    ursprungText: {
        position: "absolute",
        top: -15,
        left: 0,
        fontSize: 10,
        lineHeight: "13px",
        padding: "0 3px",
        background: "rgba(255,255,255,0.9)",
        color: "rgb(96, 96, 96)",
        whiteSpace: "nowrap",
    } as React.CSSProperties,
    zug: {
        position: "absolute",
        border: "1px dashed rgb(15, 108, 189)",
        background: "rgba(15, 108, 189, 0.1)",
        pointerEvents: "none",
    } as React.CSSProperties,
    laedt: {
        position: "absolute",
        inset: 0,
        background: "repeating-linear-gradient(135deg, rgba(15,108,189,0.15) 0 4px, rgba(15,108,189,0.05) 4px 8px)",
    } as React.CSSProperties,
    // Markierter Bereich in Aubergine — noch keine Bearbeitung, anders als der blaue Ausschnitt.
    markiert: {
        position: "absolute",
        boxSizing: "border-box",
        outline: "2px dashed rgb(125, 33, 105)",
        outlineOffset: -1,
        background: "rgba(125, 33, 105, 0.08)",
        cursor: "move",
        touchAction: "none",
        pointerEvents: "auto",
    } as React.CSSProperties,
    ecke: {
        position: "absolute",
        width: 10,
        height: 10,
        boxSizing: "border-box",
        background: "#fff",
        border: "2px solid rgb(125, 33, 105)",
        borderRadius: 2,
        touchAction: "none",
    } as React.CSSProperties,
    leiste: {
        position: "absolute",
        left: 0,
        display: "flex",
        gap: 4,
        padding: 3,
        background: "#fff",
        border: "1px solid rgba(125, 33, 105, 0.35)",
        borderRadius: 8,
        boxShadow: "0 6px 16px -8px rgba(44, 10, 38, 0.45)",
        whiteSpace: "nowrap",
        cursor: "default",
        zIndex: 2,
    } as React.CSSProperties,
};

export const AusschnittEbene: React.FC<{
    viewer: ViewerLike | null;
    container: HTMLElement | null;
    /** Werkzeug „Ausschneiden“ gewählt? Ausschnitte sind immer sichtbar, bewegt werden sie nur aktiv. */
    aktiv: boolean;
    stand: BearbeitungStand;
    dispatch: React.Dispatch<BearbeitungAktion>;
    onHinweis: (text: string | null) => void;
    /**
     * Nimmt den Bereich einer Seite als PNG auf (im Editor: `nimmAuf` mit der
     * PDF.js-Seite). `loeschen`: Bereiche, die in der Aufnahme leer bleiben —
     * zum Entfernen markierte Stellen und Quellen anderer Ausschnitte.
     */
    aufnehmen: (seite: number, rect: PdfRect, loeschen: PdfRect[]) => Promise<Blob>;
}> = ({ viewer, container, aktiv, stand, dispatch, onHinweis, aufnehmen }) => {
    const [, neuZeichnen] = React.useReducer((n: number) => n + 1, 0);
    const [zug, setZug] = React.useState<Zug | null>(null);
    const [geste, setGeste] = React.useState<Geste | null>(null);
    const [ausgewaehlt, setAusgewaehlt] = React.useState<string | null>(null);
    // Erst markieren, dann entscheiden (PR 5): der aufgezogene Bereich wartet hier auf
    // Ausschneiden/Entfernen/Verwerfen. `markiertNr` zählt neue Markierungen (Fokus).
    const [markiert, setMarkiert] = React.useState<Markiert | null>(null);
    const [markiertNr, setMarkiertNr] = React.useState(0);
    const markiertRef = React.useRef(markiert);
    markiertRef.current = markiert;
    const markiertEl = React.useRef<HTMLDivElement | null>(null);
    const hosts = React.useRef(new Map<number, HTMLDivElement>());
    const elemente = React.useRef(new Map<string, HTMLDivElement>());
    // Aktueller Stand für Rückrufe aus Fenster-Listenern (Verschieben endet außerhalb von React).
    const standRef = React.useRef(stand);
    standRef.current = stand;
    // Nach dem Verlassen des Editors keine Object-URLs mehr erzeugen (liefen sonst aus).
    const lebt = React.useRef(true);
    /** Mit Entf zurückgenommene Ausschnitte (Gruppen) — ihr Bild wird nicht mehr angelegt. */
    const verworfen = React.useRef(new Set<string>());
    React.useEffect(() => {
        lebt.current = true;
        return () => {
            lebt.current = false;
        };
    }, []);

    React.useEffect(() => {
        if (!viewer) return;
        const bus = viewer.eventBus;
        const fn = () => neuZeichnen();
        for (const e of RENDER_EREIGNISSE) bus.on(e, fn);
        return () => {
            for (const e of RENDER_EREIGNISSE) bus.off(e, fn);
        };
    }, [viewer]);

    React.useEffect(() => {
        const map = hosts.current;
        return () => {
            for (const h of map.values()) h.remove();
            map.clear();
        };
    }, []);

    // Object-URLs verworfener Ausschnitte freigeben (und beim Verlassen alle).
    const urls = React.useRef(new Set<string>());
    React.useEffect(() => {
        const jetzt = new Set(stand.einfuegen.map((e) => e.bildUrl).filter((u): u is string => !!u));
        for (const u of urls.current) if (!jetzt.has(u)) URL.revokeObjectURL(u);
        urls.current = jetzt;
    }, [stand.einfuegen]);
    React.useEffect(
        () => () => {
            for (const u of urls.current) URL.revokeObjectURL(u);
        },
        [],
    );

    React.useEffect(() => {
        if (!container) return;
        const vorher = container.style.cursor;
        if (aktiv) container.style.cursor = "crosshair";
        return () => {
            container.style.cursor = vorher;
        };
    }, [aktiv, container]);

    React.useEffect(() => {
        if (!aktiv) {
            setAusgewaehlt(null);
            setMarkiert(null);
        }
    }, [aktiv]);

    const seitenAnsicht = React.useCallback(
        (i: number): SeitenAnsichtLike | null => (viewer ? viewer.getPageView(i) ?? null : null),
        [viewer],
    );

    const hostFuer = (i: number): HTMLDivElement => {
        let host = hosts.current.get(i);
        if (!host) {
            host = document.createElement("div");
            host.className = "kmvs-ausschnitt-ebene";
            // Über der Entfernen-Ebene (20) — das Element liegt anfangs genau auf seiner Quelle.
            Object.assign(host.style, { position: "absolute", inset: "0", pointerEvents: "none", zIndex: "21" });
            hosts.current.set(i, host);
        }
        return host;
    };
    const benutzteSeiten = React.useRef(new Set<number>());

    React.useLayoutEffect(() => {
        for (const [i, host] of hosts.current) {
            const div = benutzteSeiten.current.has(i) ? seitenAnsicht(i)?.div : null;
            if (div && host.parentNode !== div) div.appendChild(host);
            else if (!div && host.parentNode) host.remove();
        }
    });

    // Neu ausgeschnittenes Element bekommt den Fokus — Pfeiltasten und Entf wirken sofort.
    React.useEffect(() => {
        if (ausgewaehlt) elemente.current.get(ausgewaehlt)?.focus({ preventScroll: true });
    }, [ausgewaehlt]);
    // Ebenso eine frische Markierung: Enter, Entf, Esc und Pfeiltasten wirken sofort.
    React.useEffect(() => {
        if (markiertNr > 0) markiertEl.current?.focus({ preventScroll: true });
    }, [markiertNr]);

    // ─── Ausschneiden ───────────────────────────────────────────────────────

    /** Liegt eine PDF.js-Anmerkung/ein Formularfeld/ein eingefügtes Element im Bereich? */
    function anmerkungenIm(seite: number, rect: PdfRect): boolean {
        const ansicht = seitenAnsicht(seite);
        if (!ansicht?.div) return false;
        const seitenBox = inhaltsBox(ansicht.div);
        const b = pdfZuCssRect(ansicht.viewport, rect);
        const bereich = {
            left: seitenBox.left + b.left,
            top: seitenBox.top + b.top,
            right: seitenBox.left + b.left + b.width,
            bottom: seitenBox.top + b.top + b.height,
        };
        return Array.from(ansicht.div.querySelectorAll<HTMLElement>(ANMERKUNGEN)).some((el) =>
            ueberlappt(el.getBoundingClientRect(), bereich),
        );
    }

    /** Quelle markieren, bewegliches Element anlegen, Bild aufnehmen. `false` = verweigert (Hinweis gesetzt). */
    const ausschneiden = React.useCallback(
        (seite: number, rect: PdfRect): boolean => {
            const jetzt = standRef.current;
            // Eine geänderte Zeile im Bereich würde als ALTER Text mitwandern und
            // die Änderung ginge verloren — lieber verweigern und erklären.
            if (jetzt.ersetzen.some((e) => e.seite === seite && schneidet(e.rect, rect))) {
                onHinweis(HINWEIS_GEAENDERTE_ZEILE);
                return false;
            }
            // Was hier schon entfernt wird (auch Quellen anderer Ausschnitte),
            // darf im Bild nicht wieder auftauchen.
            const loeschen = jetzt.entfernen.filter((m) => m.seite === seite && schneidet(m.rect, rect)).map((m) => m.rect);
            const id = neueId();
            const markierung: Markierung = { id: `q-${id}`, seite, rect, quelle: "ausschnitt", gruppe: id };
            const ausschnitt: AusschnittEintrag = { id, seite, rect, gruppe: id, bildUrl: null, blob: null };
            dispatch({ typ: "ausschneiden", markierung, ausschnitt });
            setAusgewaehlt(id);
            onHinweis(anmerkungenIm(seite, rect) ? HINWEIS_ANMERKUNGEN : null);
            aufnehmen(seite, rect, loeschen)
                .then((blob) => {
                    // Inzwischen verlassen oder mit Entf zurückgenommen → nichts mehr anlegen.
                    if (!lebt.current || verworfen.current.has(id)) return;
                    dispatch({ typ: "ausschnittBild", gruppe: id, bildUrl: URL.createObjectURL(blob), blob });
                })
                .catch((err: unknown) => {
                    if (!lebt.current) return;
                    dispatch({ typ: "ausschnittWeg", id });
                    onHinweis(
                        err instanceof Error && err.message
                            ? err.message
                            : "Der Ausschnitt konnte nicht aufgenommen werden.",
                    );
                });
            return true;
        },
        // `anmerkungenIm` hängt nur an `seitenAnsicht`.
        [aufnehmen, dispatch, onHinweis, seitenAnsicht],
    );

    /** Textauswahl → je Seite ein Ausschnitt über das Umhüllende der Zeilen. */
    const auswahlAusschneiden = React.useCallback(
        (sel: Selection) => {
            if (!viewer) return;
            const clientRects = textRechteckeDerSelection(sel);
            for (let i = 0; i < viewer.pagesCount; i++) {
                const ansicht = seitenAnsicht(i);
                if (!ansicht?.div) continue;
                const box = inhaltsBox(ansicht.div);
                const hier = clientRects.filter((r) => {
                    const mx = r.left + r.width / 2;
                    const my = r.top + r.height / 2;
                    return mx >= box.left && mx <= box.right && my >= box.top && my <= box.bottom;
                });
                const rects = auswahlZuRechtecken(hier, box, ansicht.viewport);
                if (rects.length) ausschneiden(i, umhuellende(rects));
            }
            sel.removeAllRanges();
        },
        [ausschneiden, seitenAnsicht, viewer],
    );

    // Rechteck aufziehen.
    React.useEffect(() => {
        if (!container || !aktiv || !viewer) return;
        let zugBeenden: (() => void) | null = null;
        const onDown = (e: PointerEvent) => {
            if (e.button !== 0) return;
            zugBeenden?.();
            const ziel = e.target as HTMLElement | null;
            if (!ziel || typeof ziel.closest !== "function") return;
            // Eigene Elemente, Entfernen-Markierungen, Kommentar-Symbole und -Blasen haben Vorrang.
            if (ziel.closest(".kmvs-ausschnitt-ebene, .kmvs-entfernen-ebene, .kmvs-kommentar-ebene")) return;
            const seitenDiv = ziel.closest<HTMLElement>(".page[data-page-number]");
            if (!seitenDiv) return;
            const aufText = !!ziel.closest(".textLayer span") && !ziel.classList.contains("endOfContent");
            if (aufText && !e.altKey) return;
            const seite = Number(seitenDiv.dataset.pageNumber) - 1;
            if (!seitenAnsicht(seite)) return;
            e.preventDefault();
            const box = inhaltsBox(seitenDiv);
            const start = { x: e.clientX - box.left, y: e.clientY - box.top };
            let aktuell: Zug = { seite, x0: start.x, y0: start.y, x1: start.x, y1: start.y };
            setZug(aktuell);
            setAusgewaehlt(null);
            // Ein Klick daneben oder ein neuer Zug ersetzt die wartende Markierung.
            if (markiertRef.current) {
                setMarkiert(null);
                onHinweis(null);
            }
            const klemme = (v: number, max: number) => Math.min(Math.max(v, 0), max);
            const onMove = (ev: PointerEvent) => {
                aktuell = { ...aktuell, x1: klemme(ev.clientX - box.left, box.width), y1: klemme(ev.clientY - box.top, box.height) };
                setZug(aktuell);
            };
            const beenden = () => {
                alleFenster.removeEventListener("pointermove", onMove);
                alleFenster.removeEventListener("pointerup", onUp);
                alleFenster.removeEventListener("pointercancel", beenden);
                zugBeenden = null;
                setZug(null);
            };
            const onUp = (ev: PointerEvent) => {
                onMove(ev);
                beenden();
                if (Math.abs(aktuell.x1 - aktuell.x0) < MIN_ZUG || Math.abs(aktuell.y1 - aktuell.y0) < MIN_ZUG) return;
                const ansicht = seitenAnsicht(seite);
                if (!ansicht) return;
                // Erst markieren — die Leiste am Bereich entscheidet (PR 5).
                setMarkiert({ seite, rect: cssZuPdfRect(ansicht.viewport, aktuell.x0, aktuell.y0, aktuell.x1, aktuell.y1) });
                setMarkiertNr((n) => n + 1);
                onHinweis(HINWEIS_MARKIERT);
            };
            alleFenster.addEventListener("pointermove", onMove);
            alleFenster.addEventListener("pointerup", onUp);
            alleFenster.addEventListener("pointercancel", beenden);
            zugBeenden = beenden;
        };
        container.addEventListener("pointerdown", onDown);
        return () => {
            container.removeEventListener("pointerdown", onDown);
            zugBeenden?.();
        };
    }, [aktiv, container, onHinweis, seitenAnsicht, viewer]);

    // Strg+X mit Textauswahl.
    React.useEffect(() => {
        if (!aktiv) return;
        const onKey = (e: KeyboardEvent) => {
            if (istEingabe(e.target)) return;
            const ziel = e.target as HTMLElement | null;
            if (ziel && typeof ziel.closest === "function" && ziel.closest("[role=dialog]")) return;
            if (!((e.ctrlKey || e.metaKey) && (e.key === "x" || e.key === "X"))) return;
            const sel = auswahlImViewer(container);
            if (!sel) return;
            e.preventDefault();
            auswahlAusschneiden(sel);
        };
        alleDokumente.addEventListener("keydown", onKey);
        return () => alleDokumente.removeEventListener("keydown", onKey);
    }, [aktiv, auswahlAusschneiden, container]);

    // ─── Verschieben, Skalieren, Tastatur ───────────────────────────────────

    /** Alle Seiten mit ihrer aktuellen Lage im Fenster. */
    const alleSeiten = () => {
        const liste: { seite: number; box: DOMRect; viewport: SeitenAnsichtLike["viewport"] }[] = [];
        for (let i = 0; i < (viewer?.pagesCount ?? 0); i++) {
            const ansicht = seitenAnsicht(i);
            if (ansicht?.div) liste.push({ seite: i, box: inhaltsBox(ansicht.div), viewport: ansicht.viewport });
        }
        return liste;
    };

    const verschiebenStart = (e: React.PointerEvent<HTMLDivElement>, a: AusschnittEintrag, skalieren: boolean) => {
        if (!aktiv || e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        const ansicht = seitenAnsicht(a.seite);
        if (!ansicht?.div) return;
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
        setAusgewaehlt(a.id);
        elemente.current.get(a.id)?.focus({ preventScroll: true });
        const start = { x: e.clientX, y: e.clientY };
        const boxAlt = pdfZuCssRect(ansicht.viewport, a.rect);
        const groesse = { w: a.rect[2] - a.rect[0], h: a.rect[3] - a.rect[1] };
        // Greif-Versatz: wo auf dem Element der Zeiger sitzt (relativ zur Mitte).
        const seiteStart = inhaltsBox(ansicht.div);
        const versatz = {
            x: start.x - (seiteStart.left + boxAlt.left + boxAlt.width / 2),
            y: start.y - (seiteStart.top + boxAlt.top + boxAlt.height / 2),
        };
        // Scrollt der Container während des Ziehens, wandert die Seite unter dem
        // Element weg — die Anzeige gleicht das aus, das Ziel kommt ohnehin aus
        // der Zeigerposition.
        const scrollStart = { x: container?.scrollLeft ?? 0, y: container?.scrollTop ?? 0 };
        let zeiger = { x: start.x, y: start.y };
        let letzte: Geste = { id: a.id, dx: 0, dy: 0, faktor: 1 };
        let takt: ReturnType<typeof setInterval> | null = null;
        const zeigeGeste = () => {
            if (skalieren) return;
            letzte = {
                id: a.id,
                dx: zeiger.x - start.x + ((container?.scrollLeft ?? 0) - scrollStart.x),
                dy: zeiger.y - start.y + ((container?.scrollTop ?? 0) - scrollStart.y),
                faktor: 1,
            };
            setGeste(letzte);
        };
        const autoScroll = () => {
            if (!container || skalieren) return;
            const r = container.getBoundingClientRect();
            const schritt = autoScrollSchritt(zeiger.y, { top: r.top, bottom: r.bottom });
            if (schritt === 0) {
                if (takt) clearInterval(takt);
                takt = null;
                return;
            }
            if (takt) return;
            takt = setInterval(() => {
                const rr = container.getBoundingClientRect();
                const s = autoScrollSchritt(zeiger.y, { top: rr.top, bottom: rr.bottom });
                if (s === 0) {
                    if (takt) clearInterval(takt);
                    takt = null;
                    return;
                }
                container.scrollTop += s;
                zeigeGeste();
            }, 16);
        };
        const onMove = (ev: PointerEvent) => {
            zeiger = { x: ev.clientX, y: ev.clientY };
            const dx = ev.clientX - start.x;
            const dy = ev.clientY - start.y;
            if (skalieren) {
                // Proportional: Mittel aus beiden Richtungen, nicht kleiner als 4 px.
                const f = ((boxAlt.width + dx) / boxAlt.width + (boxAlt.height + dy) / boxAlt.height) / 2;
                const minF = MIN_ZUG / Math.max(Math.min(boxAlt.width, boxAlt.height), 1);
                letzte = { id: a.id, dx: 0, dy: 0, faktor: Math.max(f, minF) };
                setGeste(letzte);
            } else {
                zeigeGeste();
                autoScroll();
            }
        };
        const ende = (ev: PointerEvent | null) => {
            if (takt) clearInterval(takt);
            takt = null;
            alleFenster.removeEventListener("pointermove", onMove);
            alleFenster.removeEventListener("pointerup", onUp);
            alleFenster.removeEventListener("pointercancel", onCancel);
            setGeste(null);
            if (!ev) return;
            const aktuell = standRef.current.einfuegen.find((x) => x.id === a.id);
            const ansichtJetzt = seitenAnsicht(a.seite);
            if (!aktuell || !ansichtJetzt?.div) return;
            if (skalieren) {
                if (Math.abs(letzte.faktor - 1) < 0.005) return;
                // Fester Punkt: die linke obere Ecke auf dem Bildschirm.
                const [ax, ay] = ansichtJetzt.viewport.convertToPdfPoint(boxAlt.left, boxAlt.top);
                const f = letzte.faktor;
                const r = aktuell.rect;
                const neu = normalisiere([ax + (r[0] - ax) * f, ay + (r[1] - ay) * f, ax + (r[2] - ax) * f, ay + (r[3] - ay) * f]);
                dispatch({ typ: "ausschnittVerschieben", id: a.id, seite: a.seite, rect: neu.map(r2) as PdfRect });
                return;
            }
            // `letzte` enthält auch das Scrollen während des Ziehens.
            if (Math.hypot(letzte.dx, letzte.dy) < MIN_VERSCHIEBUNG) return;
            // Zielseite = Seite unter der Mitte — aus der AKTUELLEN Zeigerposition.
            const ziel = zielDesAusschnitts({ x: ev.clientX, y: ev.clientY }, versatz, alleSeiten(), groesse);
            if (!ziel) return; // neben die Seiten gezogen → bleibt, wo er war
            dispatch({ typ: "ausschnittVerschieben", id: a.id, seite: ziel.seite, rect: ziel.rect });
        };
        const onUp = (ev: PointerEvent) => {
            onMove(ev);
            ende(ev);
        };
        const onCancel = () => ende(null);
        alleFenster.addEventListener("pointermove", onMove);
        alleFenster.addEventListener("pointerup", onUp);
        alleFenster.addEventListener("pointercancel", onCancel);
    };

    const tasteAmElement = (e: React.KeyboardEvent<HTMLDivElement>, a: AusschnittEintrag) => {
        if (!aktiv) return;
        if (e.key === "Delete" || e.key === "Backspace") {
            e.preventDefault();
            e.stopPropagation();
            verworfen.current.add(a.gruppe);
            dispatch({ typ: "ausschnittWeg", id: a.id });
            setAusgewaehlt(null);
            return;
        }
        if (e.key === "Escape") {
            setAusgewaehlt(null);
            (e.currentTarget as HTMLElement).blur();
            return;
        }
        const richtung: Record<string, [number, number]> = {
            ArrowLeft: [-1, 0],
            ArrowRight: [1, 0],
            ArrowUp: [0, -1],
            ArrowDown: [0, 1],
        };
        const r = richtung[e.key];
        if (!r) return;
        const ansicht = seitenAnsicht(a.seite);
        if (!ansicht) return;
        e.preventDefault();
        e.stopPropagation();
        const pt = e.shiftKey ? 10 : 1;
        const [ux, uy] = richtungImUserspace(ansicht, r[0], r[1]);
        const [x0, y0, x1, y1] = a.rect;
        dispatch({
            typ: "ausschnittVerschieben",
            id: a.id,
            seite: a.seite,
            rect: [r2(x0 + ux * pt), r2(y0 + uy * pt), r2(x1 + ux * pt), r2(y1 + uy * pt)],
        });
    };

    // ─── Markierter Bereich: anpassen und entscheiden ───────────────────────

    const markiertVerwerfen = () => {
        setMarkiert(null);
        onHinweis(null);
    };
    const markiertAusschneiden = () => {
        const m = markiertRef.current;
        if (!m) return;
        // Verweigert (geänderte Zeile im Bereich) → die Markierung bleibt zum Anpassen stehen.
        if (ausschneiden(m.seite, m.rect)) setMarkiert(null);
    };
    const markiertEntfernen = () => {
        const m = markiertRef.current;
        if (!m) return;
        setMarkiert(null);
        dispatch({ typ: "entfernenHinzu", markierungen: [{ id: neueId(), seite: m.seite, rect: m.rect, quelle: "rechteck" }] });
        onHinweis(null);
    };

    /** Rahmen ziehen = verschieben (innerhalb der Seite), Ecke ziehen = Größe ändern. */
    const markiertZiehen = (e: React.PointerEvent<HTMLDivElement>, ecke: Ecke | null) => {
        const m = markiertRef.current;
        if (!aktiv || !m || e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        const ansicht = seitenAnsicht(m.seite);
        if (!ansicht?.div) return;
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
        markiertEl.current?.focus({ preventScroll: true });
        const start = { x: e.clientX, y: e.clientY };
        const boxAlt = pdfZuCssRect(ansicht.viewport, m.rect);
        const seitenBox = inhaltsBox(ansicht.div);
        const klemmeX = (v: number) => Math.min(Math.max(v, 0), seitenBox.width);
        const klemmeY = (v: number) => Math.min(Math.max(v, 0), seitenBox.height);
        const onMove = (ev: PointerEvent) => {
            const dx = ev.clientX - start.x;
            const dy = ev.clientY - start.y;
            let l = boxAlt.left;
            let t = boxAlt.top;
            let r = boxAlt.left + boxAlt.width;
            let b = boxAlt.top + boxAlt.height;
            if (!ecke) {
                const ddx = Math.min(Math.max(dx, -l), seitenBox.width - r);
                const ddy = Math.min(Math.max(dy, -t), seitenBox.height - b);
                l += ddx;
                r += ddx;
                t += ddy;
                b += ddy;
            } else {
                if (ecke.includes("w")) l = klemmeX(l + dx);
                else r = klemmeX(r + dx);
                if (ecke.includes("n")) t = klemmeY(t + dy);
                else b = klemmeY(b + dy);
            }
            if (Math.abs(r - l) < MIN_ZUG || Math.abs(b - t) < MIN_ZUG) return;
            setMarkiert({ seite: m.seite, rect: cssZuPdfRect(ansicht.viewport, l, t, r, b) });
        };
        const ende = () => {
            alleFenster.removeEventListener("pointermove", onMove);
            alleFenster.removeEventListener("pointerup", onUp);
            alleFenster.removeEventListener("pointercancel", ende);
        };
        const onUp = (ev: PointerEvent) => {
            onMove(ev);
            ende();
        };
        alleFenster.addEventListener("pointermove", onMove);
        alleFenster.addEventListener("pointerup", onUp);
        alleFenster.addEventListener("pointercancel", ende);
    };

    const tasteAnMarkierung = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const m = markiertRef.current;
        if (!aktiv || !m) return;
        if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            markiertAusschneiden();
            return;
        }
        if (e.key === "Delete" || e.key === "Backspace" || e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            markiertVerwerfen();
            return;
        }
        const richtung: Record<string, [number, number]> = {
            ArrowLeft: [-1, 0],
            ArrowRight: [1, 0],
            ArrowUp: [0, -1],
            ArrowDown: [0, 1],
        };
        const r = richtung[e.key];
        if (!r) return;
        const ansicht = seitenAnsicht(m.seite);
        if (!ansicht) return;
        e.preventDefault();
        e.stopPropagation();
        const pt = e.shiftKey ? 10 : 1;
        const [ux, uy] = richtungImUserspace(ansicht, r[0], r[1]);
        const [x0, y0, x1, y1] = m.rect;
        setMarkiert({ seite: m.seite, rect: [r2(x0 + ux * pt), r2(y0 + uy * pt), r2(x1 + ux * pt), r2(y1 + uy * pt)] });
    };

    // ─── Zeichnen ───────────────────────────────────────────────────────────

    if (!viewer) return null;

    const quellen = stand.entfernen.filter((m) => m.quelle === "ausschnitt");
    const seiten = new Set<number>([...quellen.map((m) => m.seite), ...stand.einfuegen.map((e) => e.seite)]);
    if (zug) seiten.add(zug.seite);
    if (markiert) seiten.add(markiert.seite);
    benutzteSeiten.current = seiten;

    const titel = aktiv
        ? "Ausschnitt — ziehen oder mit den Pfeiltasten verschieben (Umschalt = 10 pt), Ecke skaliert, Entf nimmt das Ausschneiden zurück. Wird als Bild verschoben."
        : "Ausschnitt — zum Verschieben das Werkzeug „Ausschneiden“ wählen";

    return (
        <>
            {[...seiten].map((seite) => {
                const ansicht = seitenAnsicht(seite);
                if (!ansicht?.div) return null;
                const host = hostFuer(seite);
                const drehung = drehungVon(ansicht);
                return createPortal(
                    <>
                        {quellen
                            .filter((m) => m.seite === seite)
                            .map((m) => {
                                const box = pdfZuCssRect(ansicht.viewport, m.rect);
                                return (
                                    <div key={m.id} data-ursprung={m.gruppe} style={{ ...stil.ursprung, ...box }}>
                                        <span style={stil.ursprungText}>Ursprung</span>
                                    </div>
                                );
                            })}
                        {stand.einfuegen
                            .filter((a) => a.seite === seite)
                            .map((a) => {
                                const box = pdfZuCssRect(ansicht.viewport, a.rect);
                                const g = geste?.id === a.id ? geste : null;
                                const breite = box.width * (g?.faktor ?? 1);
                                const hoehe = box.height * (g?.faktor ?? 1);
                                const quer = drehung % 180 !== 0;
                                return (
                                    <div
                                        key={a.id}
                                        ref={(el) => {
                                            if (el) elemente.current.set(a.id, el);
                                            else elemente.current.delete(a.id);
                                        }}
                                        data-ausschnitt={a.id}
                                        role="img"
                                        aria-label="Verschiebbarer Ausschnitt"
                                        title={titel}
                                        tabIndex={aktiv ? 0 : -1}
                                        onPointerDown={(e) => verschiebenStart(e, a, false)}
                                        onKeyDown={(e) => tasteAmElement(e, a)}
                                        onFocus={() => aktiv && setAusgewaehlt(a.id)}
                                        style={{
                                            ...stil.element,
                                            ...(ausgewaehlt === a.id && aktiv ? stil.elementAusgewaehlt : null),
                                            pointerEvents: aktiv ? "auto" : "none",
                                            left: box.left,
                                            top: box.top,
                                            width: breite,
                                            height: hoehe,
                                            transform: g ? `translate(${g.dx}px, ${g.dy}px)` : undefined,
                                            zIndex: g ? 1 : undefined,
                                        }}
                                    >
                                        {a.bildUrl ? (
                                            <img
                                                src={a.bildUrl}
                                                alt=""
                                                draggable={false}
                                                style={{
                                                    position: "absolute",
                                                    left: "50%",
                                                    top: "50%",
                                                    // Das Bild ist ungedreht (Userspace) aufgenommen — in einer
                                                    // gedrehten Ansicht dreht es mit.
                                                    width: quer ? hoehe : breite,
                                                    height: quer ? breite : hoehe,
                                                    transform: `translate(-50%, -50%) rotate(${drehung}deg)`,
                                                    pointerEvents: "none",
                                                    userSelect: "none",
                                                }}
                                            />
                                        ) : (
                                            <div style={stil.laedt} aria-label="Ausschnitt wird aufgenommen" />
                                        )}
                                        {aktiv && ausgewaehlt === a.id && (
                                            <div
                                                data-griff=""
                                                title="Größe ändern (proportional)"
                                                style={stil.griff}
                                                onPointerDown={(e) => verschiebenStart(e, a, true)}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        {markiert?.seite === seite &&
                            (() => {
                                const box = pdfZuCssRect(ansicht.viewport, markiert.rect);
                                // Leiste über dem Bereich — am oberen Seitenrand darunter.
                                const leisteOben = box.top >= 36;
                                return (
                                    <div
                                        ref={markiertEl}
                                        data-markiert=""
                                        role="group"
                                        aria-label="Markierter Bereich"
                                        tabIndex={0}
                                        title="Markierter Bereich — ziehen verschiebt, die Ecken ändern die Größe; Enter schneidet aus, Entf oder Esc verwirft"
                                        style={{ ...stil.markiert, left: box.left, top: box.top, width: box.width, height: box.height }}
                                        onPointerDown={(e) => markiertZiehen(e, null)}
                                        onKeyDown={tasteAnMarkierung}
                                    >
                                        {ECKEN.map((ecke) => (
                                            <div
                                                key={ecke}
                                                data-ecke={ecke}
                                                style={{ ...stil.ecke, ...ECKEN_LAGE[ecke] }}
                                                onPointerDown={(e) => markiertZiehen(e, ecke)}
                                            />
                                        ))}
                                        <div
                                            className="pdfx pdfx-mini"
                                            data-markiert-leiste=""
                                            role="toolbar"
                                            aria-label="Markierter Bereich"
                                            style={{ ...stil.leiste, ...(leisteOben ? { bottom: "100%", marginBottom: 6 } : { top: "100%", marginTop: 6 }) }}
                                            onPointerDown={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => e.stopPropagation()}
                                        >
                                            <button
                                                type="button"
                                                className="primary"
                                                title="Als Bild verschieben — die Quelle wird beim Speichern entfernt (Enter)"
                                                onClick={markiertAusschneiden}
                                            >
                                                Ausschneiden
                                            </button>
                                            <button
                                                type="button"
                                                title="Bereich beim Speichern aus dieser Fassung entfernen"
                                                onClick={markiertEntfernen}
                                            >
                                                Entfernen
                                            </button>
                                            <button type="button" aria-label="Verwerfen" title="Markierung verwerfen (Esc)" onClick={markiertVerwerfen}>
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                );
                            })()}
                        {zug?.seite === seite && (
                            <div
                                data-zugrahmen=""
                                style={{
                                    ...stil.zug,
                                    left: Math.min(zug.x0, zug.x1),
                                    top: Math.min(zug.y0, zug.y1),
                                    width: Math.abs(zug.x1 - zug.x0),
                                    height: Math.abs(zug.y1 - zug.y0),
                                }}
                            />
                        )}
                    </>,
                    host,
                    `ausschnitt-seite-${seite}`,
                );
            })}
        </>
    );
};
